import { type TypedDocumentNode, gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedNumber } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { BrandS, BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { RankingRatio } from '@sorare/core/src/components/collections/RankingRatio';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import type { CollectionPreviewHeader_cardCollection } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  height: 100%;
  overflow: hidden;
`;
const ClubLogo = styled.img`
  height: 32px;
  object-fit: contain;
`;

const nameStyles = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NameSmall = styled(BrandXS)`
  ${nameStyles}
`;
const NameMedium = styled(BrandS)`
  ${nameStyles}
`;

const Stats = styled(Horizontal)`
  align-items: flex-end;
`;

const RightContent = styled(Vertical)`
  overflow: hidden;
`;

const StyledCircularProgress = styled(CircularProgress)`
  width: 12px;
  height: 12px;
`;

type Props = {
  cardCollection: Nullable<CollectionPreviewHeader_cardCollection>;
  small?: boolean;
};

export const CollectionPreviewHeader = ({ cardCollection, small }: Props) => {
  const { anyTeam, userCardCollection } = cardCollection || {};
  const { liveRankingRatio, liveRanking } = userCardCollection || {};
  const { pictureUrl, shortName } = anyTeam || {};
  const domesticLeague = isType(anyTeam, 'Club')
    ? anyTeam.domesticLeague
    : null;

  const fulfilledSlotsCount = userCardCollection?.fulfilledSlotsCount ?? 0;
  const slotsCount = userCardCollection?.slotsCount ?? 1;
  const Name = small ? NameSmall : NameMedium;
  return (
    <Root>
      {!!pictureUrl && <ClubLogo src={pictureUrl} alt="" />}
      <RightContent gap={0.5}>
        <Name>{shortName}</Name>
        <Stats>
          <StyledCircularProgress
            progress={fulfilledSlotsCount}
            maxProgress={slotsCount}
            style={
              {
                '--circular-progress-stroke':
                  COMPETITION_COLORS[domesticLeague?.slug || ''],
              } as CSSProperties
            }
          />
          <LabelS color="var(--c-nd-600)">
            <FormattedNumber
              value={fulfilledSlotsCount / slotsCount}
              maximumFractionDigits={0}
              style="percent"
            />
          </LabelS>
          <LabelS color="var(--c-nd-600)">
            <RankingRatio
              liveRankingRatio={liveRankingRatio || 0}
              liveRanking={liveRanking || 0}
            />
          </LabelS>
        </Stats>
      </RightContent>
    </Root>
  );
};

CollectionPreviewHeader.fragments = {
  cardCollection: gql`
    fragment CollectionPreviewHeader_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        shortName
        pictureUrl(derivative: "avatar")
        ... on Club {
          slug
          domesticLeague {
            slug
          }
        }
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        slotsCount
        fulfilledSlotsCount
        liveRankingRatio
        liveRanking
      }
    }
  ` as TypedDocumentNode<CollectionPreviewHeader_cardCollection>,
};
